import styled, { css, keyframes } from 'styled-components'

import {
  THEME,
  COLORS,
  typeScale,
  media,
  focus,
  TEMPLATE_PSEUDO_CLASSES,
  focusSelector,
  CSS_PSEUDO_CLASSES_RESET,
  hoverSelector,
  CSS_ELEMENTS_RESETS,
} from '../../styles'
import Icon from '../Icon'
import Ingredient from '../Ingredient'
import RecipePoster from '../RecipePoster'
import SpinnerLoader from '../SpinnerLoader'

const Styled = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  padding-top: ${({ wasPlayed }) => (wasPlayed ? '0' : '100%')};
  ${media.medium`
    padding-top: 0;
  `}
`
Styled.videoPlayer = styled.div`
  display: ${({ wasPlayed }) => (wasPlayed ? 'flex' : 'none')};
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  ${media.medium`
    height: 100%;
  `}
`
Styled.videoContainer = styled.section`
  position: relative;
  width: 100%;
  padding-top: ${({ wasPlayed }) => (wasPlayed ? '56.25%' : '100%')};
  overflow: hidden;
  ${media.medium`
    padding-top: 0;
    flex: 1;
  `}
`
Styled.videoWrapper = styled.div`
  position: absolute;
  inset: 0;
  ${({ posterUrl }) =>
    posterUrl
      ? `
    background-image: url("${posterUrl}");
    background-position: center center;
    background-repeat: no-repeat;
    background-size: auto 100%;
  `
      : ''};
  ${({ wasPlayed, backgroundColor }) => media.medium`
    position: relative;
    top: unset;
    left: unset;
    bottom: unset;
    right: unset;
    width: 100%;
    height: 100%;
    background-color: ${
      wasPlayed
        ? THEME.COLORS.foreground.secondary
        : backgroundColor || THEME.COLORS.foreground.secondary
    };
    background-image: unset;
  `}
`
Styled.video = styled.video`
  width: auto;
  height: 100%;
  max-width: 100%;
  object-fit: contain;
  margin: 0 auto;
  display: block;
`
Styled.videoControls = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: row nowrap;
`
Styled.videoResumeButtonIcon = styled(Icon)`
  width: 3.6rem;
  height: 3.6rem;
`
Styled.videoResumeButtonSpinner = styled(SpinnerLoader)``
Styled.videoResumeButton = styled.div`
  margin: 0 auto;
  display: block;
  border: 0;
  border-radius: 9999px;
  height: 7.2rem;
  width: 7.2rem;
  padding: 1.8rem;
  background-color: ${({ isPlaying }) =>
    isPlaying ? THEME.COLORS.foreground.secondary : COLORS.primary};
  opacity: ${({ isPlaying }) => (isPlaying ? 0 : 1)};
  transition:
    opacity 0.2s ease-in-out,
    background-color 0.2s ease-in-out;
  -webkit-tap-highlight-color: ${COLORS.transparent};
  ${focus`
    outline-color: ${COLORS.white};
  `}
`
Styled.videoPlay = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  ${focus`
    ${CSS_PSEUDO_CLASSES_RESET.focus};
  `}
  ${({ isPlaying }) => hoverSelector(Styled.videoResumeButton)`
    opacity: 1;
    background-color: ${
      isPlaying ? THEME.COLORS.foreground.secondary : COLORS.primary
    };
  `}
  ${focusSelector(Styled.videoResumeButton)`
    ${TEMPLATE_PSEUDO_CLASSES.focus};
    outline-color: ${COLORS.white};
    opacity: 1;
  `}
`
Styled.videoControlIconContainer = styled.div`
  background-color: ${({ backgroundColor }) => backgroundColor || COLORS.kale};
  border-radius: 9999px;
  padding: 0.8rem;
  width: 4.8rem;
  height: 4.8rem;
`
Styled.videoControlIcon = styled(Icon)`
  transform: rotate(${({ position }) => (position === 'left' ? 0 : '180deg')});
  width: 3.2rem;
  height: 3.2rem;
`
Styled.videoControl = styled.button`
  padding: ${({ position }) =>
    position === 'left' ? '1.6rem 0 1.6rem 1.6rem' : '1.6rem 1.6rem 1.6rem 0'};
  background: none;
  border: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transition: opacity 0.2s ease-out;
  cursor: pointer;
  -webkit-tap-highlight-color: ${COLORS.transparent};

  &:disabled {
    opacity: 0;
    cursor: default;
  }
  ${({ position }) => media.medium`
    padding: ${
      position === 'left' ? '3.6rem 0 3.6rem 3.6rem' : '3.6rem 3.6rem 3.6rem 0'
    };
  `}
  ${focus`
    ${CSS_PSEUDO_CLASSES_RESET.focus};
  `}
  ${focusSelector(Styled.videoControlIcon)`
    ${TEMPLATE_PSEUDO_CLASSES.focus};
    outline-color: ${COLORS.white};
  `}
`
Styled.subtitles = styled.button`
  ${CSS_ELEMENTS_RESETS.button};
  position: absolute;
  right: 1.6rem;
  bottom: 1.6rem;
`
Styled.sutitleIcon = styled(Icon)`
  width: 2.4rem;
  height: 2.4rem;
`
Styled.videoBench = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  padding: 0.4rem 0.2rem;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  flex-flow: row nowrap;
`
Styled.videoBenchStep = styled.button`
  background: rgba(255, 255, 255, ${({ active }) => (active ? 1 : 0.33)});
  border: 0;
  height: ${({ currentActive }) => (currentActive ? '1.6rem' : '0.8rem')};
  margin: 0 0.2rem;
  transition: height 0.2s ease-out;
  width: ${({ count }) => `calc((100% / ${count}) - 0.4rem)`};
  padding: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: ${COLORS.transparent};
`
const progress = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`
Styled.videoBenchProgress = styled.span`
  height: 100%;
  background: ${COLORS.white};
  width: ${({ passed, active }) => (passed && !active ? '100%' : 0)};
  display: block;
  ${({ active }) =>
    active
      ? css`
          animation: ${progress} ${({ duration }) => duration}s linear forwards;
        `
      : ''};
`
Styled.step = styled.div`
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${COLORS.white};
  flex: 1 1 auto;
  width: 100%;
  text-align: center;
  ${media.medium`
    height: 28rem;
    flex: 0 0 28rem;
  `}
`
Styled.stepContent = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: column nowrap;
  padding: 2.4rem;
  overflow: auto;
`
Styled.stepTitle = styled.p`
  ${typeScale('body')};
  color: ${({ textColor }) => textColor || COLORS.white};
`
Styled.stepIngredients = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-flow: row wrap;
  width: 100%;
  padding-top: 1.6rem;
  ${media.medium`
    padding-top: 2.4rem;
    flex-flow: row nowrap;
  `}
`
Styled.stepIngredient = styled(Ingredient)`
  margin: 0 0.4rem 0.4rem;

  img {
    margin: 0 auto 0.4rem;
    height: 4.8rem;
    width: 4.8rem;
  }
  ${media.medium`
    margin: 0 0.8rem 0.8rem;
  `}
`
Styled.recipePoster = styled(RecipePoster)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`

export default Styled
